import * as THREE from "three";
import type { CameraConfig } from "../../cameraConfig";
import type { SceneControls } from "../controls/SceneControls";
import {
  cartesianToSpherical,
  shortestAngleDelta,
  sphericalToCartesian,
  type Spherical3,
} from "./cameraMath";
import { easeInOutCubic, easeOutCubic } from "../../utils/easing";

export type CameraPose = {
  position: THREE.Vector3;
  target: THREE.Vector3;
  fov?: number;
};

export type OrbitAnimationConfig = {
  sightPosition: THREE.Vector3;
  distance: number;
  height: number;
  azimuthMin: number;
  azimuthMax: number;
  polarMin: number;
  polarMax: number;
};

export type TransitionEasing = 'ease-in-out' | 'ease-out';

/**
 * Trajectory shape for camera pose transitions.
 *
 * - `linear`   — `Vector3.lerp` on `position` and `target` independently;
 *                orientation is re-computed each frame via `controls.lookAt`.
 *                Default for cinematic uses (PreviewTour, CameraFlyover skip,
 *                nav-POI fly-in, OverallSceneOverlay swipe).
 * - `orbitArc` — interpolates `radius / azimuth / polar` around the moving
 *                `target` so the camera sweeps an arc instead of a chord.
 *                Used for POI clicks in orbit scenes (kaaba / arafat /
 *                muzdalifah / overall) where a chord can pierce
 *                `orbitMinDistance` or `clampMaxHeight`.
 * - `fpsSlerp` — linear `Vector3.lerp` on position + `Quaternion.slerp` on
 *                orientation. The endpoint quaternion is precomputed once
 *                from `(endPosition → endTarget)` with optional vertical
 *                constraint baked in; per-frame slerp gives a constant
 *                angular speed (subject to easing) regardless of how far
 *                the start/end lookAt vectors differ in magnitude. Used
 *                for POI clicks in FPS scenes (mina / kaaba-inside) where
 *                the start "synthetic forward" target sits 1 unit from
 *                the camera while the end target can be 5–10 units away —
 *                lerp'ing those produces a non-uniform angular trajectory
 *                that reads as a "snap turn" near the start.
 */
export type TransitionInterpolation = 'linear' | 'orbitArc' | 'fpsSlerp';

const _transitionScratchPos = new THREE.Vector3();
const _transitionScratchTarget = new THREE.Vector3();
const _transitionScratchForward = new THREE.Vector3();
/**
 * Scratch matrix for deriving an `fpsSlerp` endQuaternion via
 * `Matrix4.lookAt(eye, target, up)`. We use `Matrix4` directly instead of
 * `Object3D.lookAt` because `Object3D.lookAt` swaps `(eye, target)` for
 * non-camera/light instances, producing a 180°-flipped orientation.
 * `Matrix4.lookAt` is type-agnostic — `(eye, target, up)` is always
 * `(eye, target, up)`.
 */
const _transitionScratchMatrix = new THREE.Matrix4();

export class CameraDirector {
  private camera: THREE.PerspectiveCamera;
  private controls: SceneControls;
  private cameraConfig: CameraConfig;
  private isTransitioning: boolean = false;
  private controlsWasEnabled: boolean = true;
  private transitionStartTime: number = 0;
  private transitionDuration: number = 0;
  private transitionEasing: TransitionEasing = 'ease-in-out';
  private startPosition: THREE.Vector3 = new THREE.Vector3();
  private endPosition: THREE.Vector3 = new THREE.Vector3();
  private startTarget: THREE.Vector3 = new THREE.Vector3();
  private endTarget: THREE.Vector3 = new THREE.Vector3();
  private startFov: number = 0;
  private endFov: number = 0;
  private transitionApplyVerticalConstraint: boolean = true;
  private transitionApplyHeightClamp: boolean = true;
  private transitionInterpolation: TransitionInterpolation = 'linear';
  /** Spherical state of the starting pose, relative to `startTarget`. */
  private startSpherical: Spherical3 = { radius: 0, azimuth: 0, polar: 0 };
  /** Spherical state of the ending pose, relative to `endTarget`. */
  private endSpherical: Spherical3 = { radius: 0, azimuth: 0, polar: 0 };
  /**
   * `shortestAngleDelta(startSpherical.azimuth, endSpherical.azimuth)` —
   * precomputed so per-frame interpolation just lerps with this delta and
   * doesn't redo `atan2` / branch handling.
   */
  private azimuthDelta: number = 0;
  /** Camera orientation at the moment `fpsSlerp` transition started. */
  private startQuaternion: THREE.Quaternion = new THREE.Quaternion();
  /** Final camera orientation for an `fpsSlerp` transition (constraint-baked). */
  private endQuaternion: THREE.Quaternion = new THREE.Quaternion();
  private onComplete?: () => void;
  private lastPosePosition: THREE.Vector3 = new THREE.Vector3();
  private lastPoseTarget: THREE.Vector3 = new THREE.Vector3();
  private lastPoseFov: number = 0;
  private hasPose: boolean = false;

  private orbitAnimationEnabled: boolean = false;
  private orbitAnimationActive: boolean = false;
  private orbitAnimationStartTime: number = -1;
  private orbitAnimationBlendStartTime: number = -1;
  private orbitAnimationConfig: OrbitAnimationConfig | null = null;
  private orbitAnimationPosition: THREE.Vector3 = new THREE.Vector3();
  private orbitAnimationBlendedPosition: THREE.Vector3 = new THREE.Vector3();
  private orbitAnimationTarget: THREE.Vector3 = new THREE.Vector3();
  private orbitAnimationBlendStartTarget: THREE.Vector3 = new THREE.Vector3();
  private orbitAnimationBlendedTarget: THREE.Vector3 = new THREE.Vector3();
  private orbitAnimationBlendStartPosition: THREE.Vector3 = new THREE.Vector3();
  private orbitAnimationBlendDuration = 0.8;
  private orbitAnimationPhaseOffset = 0;

  private readonly orbitAnimationPeriod = 20;

  constructor(
    camera: THREE.PerspectiveCamera,
    controls: SceneControls,
    cameraConfig: CameraConfig
  ) {
    this.camera = camera;
    this.controls = controls;
    this.cameraConfig = { ...cameraConfig };
  }

  update(currentTime: number): void {
    if (this.isTransitioning) {
      this.updateTransition(currentTime);
      return;
    }

    if (
      this.orbitAnimationActive &&
      this.orbitAnimationConfig &&
      this.controls.viewMode === "orbit" &&
      this.controls.enabled
    ) {
      if (this.orbitAnimationStartTime < 0) {
        this.orbitAnimationStartTime = currentTime;
      }
      this.updateOrbitAnimation(currentTime);
    }
  }

  syncPoseFromCamera(): void {
    if (this.isTransitioning) {
      return;
    }
    this.lastPosePosition.copy(this.camera.position);
    if (this.controls.viewMode === "orbit") {
      this.lastPoseTarget.copy(this.controls.orbit.target);
    } else {
      const direction = new THREE.Vector3(0, 0, -1).applyQuaternion(
        this.camera.quaternion
      );
      this.lastPoseTarget.copy(this.camera.position).add(direction);
    }
    this.lastPoseFov = this.camera.fov;
    this.hasPose = true;
  }

  isInTransition(): boolean {
    return this.isTransitioning;
  }

  cancelTransition(): void {
    this.isTransitioning = false;
    this.controls.enabled = this.controlsWasEnabled;
    this.onComplete = undefined;
  }

  moveToPose(pose: CameraPose, applyVerticalConstraint: boolean = true): void {
    this.applyPose(pose, applyVerticalConstraint);
  }

  transitionToPose(
    pose: CameraPose,
    duration: number,
    onComplete?: () => void,
    currentTime?: number,
    applyVerticalConstraint: boolean = true,
    easing: TransitionEasing = 'ease-in-out',
    applyHeightClamp: boolean = true,
    interpolation: TransitionInterpolation = 'linear',
  ): void {
    if (this.isTransitioning) {
      this.cancelTransition();
    }

    this.controlsWasEnabled = this.controls.enabled;
    this.controls.enabled = false;
    // Disable Three.js native auto-rotate so accumulated `sphericalDelta`
    // doesn't keep getting topped up during the transition. Background:
    // `applyPose` calls `orbit.setTarget(target)` every frame, which
    // internally calls `controls.update()` (Three.js). Three.js'
    // `OrbitControls.update()` has NO early-return on `enabled === false`
    // (only its input handlers respect that flag). It still runs the body,
    // and inside the body `if (autoRotate && state === NONE)` adds to
    // `sphericalDelta`. With damping, the delta reaches a non-zero
    // steady state. Result: by the time transition completes, the camera
    // pose is biased by that steady-state delta (~0.064 rad of azimuth
    // shift → ~0.03 unit position shift for a 7-unit orbit). The bias
    // surfaces as a visible camera jitter when `restoreOrbitLocks()` then
    // clamps spherical back to the locked range on the next update().
    this.controls.orbit.setAutoRotate(false, 0);

    if (this.hasPose) {
      this.startPosition.copy(this.lastPosePosition);
      this.startTarget.copy(this.lastPoseTarget);
      this.startFov = this.lastPoseFov || this.camera.fov;
    } else {
      const currentDirection = new THREE.Vector3(0, 0, -1);
      currentDirection.applyQuaternion(this.camera.quaternion);
      const currentTarget = this.camera.position.clone().add(currentDirection);
      this.controls.lookAt(currentTarget);
      this.startPosition.copy(this.camera.position);
      this.startTarget.copy(currentTarget);
      this.startFov = this.camera.fov;
    }

    this.endPosition.copy(pose.position);
    this.endTarget.copy(pose.target);
    this.endFov = pose.fov ?? this.camera.fov;
    this.transitionApplyVerticalConstraint = applyVerticalConstraint;
    this.transitionApplyHeightClamp = applyHeightClamp;
    this.transitionInterpolation = interpolation;

    // For orbit-arc: pre-clamp the endpoint's Y once instead of clamping
    // every frame inside `applyPose`. Mid-arc samples lie between the
    // endpoints' radius/polar values, so y(t) stays within the band by
    // construction. Linear mode keeps per-frame clamping (unchanged) so
    // cinematic callers' visuals don't drift.
    if (
      interpolation === 'orbitArc'
      && applyHeightClamp
      && (this.cameraConfig.clampMinHeight !== -Infinity
        || this.cameraConfig.clampMaxHeight !== Infinity)
    ) {
      this.endPosition.y = THREE.MathUtils.clamp(
        this.endPosition.y,
        this.cameraConfig.clampMinHeight,
        this.cameraConfig.clampMaxHeight,
      );
    }

    if (interpolation === 'orbitArc') {
      this.startSpherical = cartesianToSpherical(this.startTarget, this.startPosition);
      this.endSpherical = cartesianToSpherical(this.endTarget, this.endPosition);
      // Degenerate radii (camera ≈ target) — can't define azimuth/polar
      // for one of the endpoints. Fall back to linear so we never produce
      // NaN. In practice this won't fire for backend POIs because their
      // `distance` is always > 0.
      if (this.startSpherical.radius < 1e-4 || this.endSpherical.radius < 1e-4) {
        this.transitionInterpolation = 'linear';
        this.azimuthDelta = 0;
      } else {
        this.azimuthDelta = shortestAngleDelta(
          this.startSpherical.azimuth,
          this.endSpherical.azimuth,
        );
      }
    } else if (interpolation === 'fpsSlerp') {
      // Snapshot current orientation as the slerp source.
      this.startQuaternion.copy(this.camera.quaternion);

      // Derive the end orientation from the endPose with vertical
      // constraint pre-applied so the slerp stays inside the configured
      // verticalMin/Max cone.
      const constrainedEndTarget =
        applyVerticalConstraint && this.cameraConfig.constrainVertical
          ? this.constrainTargetVerticalAngle(this.endPosition, this.endTarget)
          : this.endTarget;
      _transitionScratchMatrix.lookAt(
        this.endPosition,
        constrainedEndTarget,
        this.camera.up,
      );
      this.endQuaternion.setFromRotationMatrix(_transitionScratchMatrix);
    }

    this.transitionDuration = Math.max(duration, 0.0001);
    this.transitionStartTime = currentTime ?? -1;
    this.transitionEasing = easing;
    this.isTransitioning = true;
    this.onComplete = onComplete;
  }

  setOrbitAnimationEnabled(enabled: boolean): void {
    this.orbitAnimationEnabled = enabled;
    if (!enabled) {
      this.stopOrbitAnimation();
    }
  }

  startOrbitAnimation(config: OrbitAnimationConfig): void {
    if (!this.orbitAnimationEnabled) {
      return;
    }
    this.orbitAnimationConfig = { ...config, sightPosition: config.sightPosition.clone() };
    this.orbitAnimationActive = true;
    this.orbitAnimationStartTime = -1;
    this.orbitAnimationBlendStartTime = -1;
    this.orbitAnimationBlendStartPosition.copy(this.camera.position);
    this.orbitAnimationBlendStartTarget.copy(this.controls.orbit.target);
    this.orbitAnimationPhaseOffset = this.findClosestOrbitPhase(config);
    this.updateOrbitAnimationBlendDuration();
    this.controls.setOrbitAnimationActive(true);
    this.orbitAnimationTarget.copy(config.sightPosition);
  }

  stopOrbitAnimation(): void {
    if (!this.orbitAnimationActive) {
      return;
    }
    this.orbitAnimationActive = false;
    this.orbitAnimationStartTime = -1;
    this.orbitAnimationBlendStartTime = -1;
    this.orbitAnimationConfig = null;
    this.orbitAnimationPhaseOffset = 0;
    this.controls.setOrbitAnimationActive(false);
  }

  setCameraConfig(config: CameraConfig): void {
    this.cameraConfig = { ...config };
    this.applyCameraConstraints();
  }

  private updateTransition(currentTime: number): void {
    if (this.transitionStartTime < 0) {
      this.transitionStartTime = currentTime;
    }
    const elapsed = currentTime - this.transitionStartTime;
    const progress = Math.min(elapsed / this.transitionDuration, 1.0);
    const easedProgress = this.applyEasing(progress);

    const fov =
      this.startFov !== this.endFov
        ? THREE.MathUtils.lerp(this.startFov, this.endFov, easedProgress)
        : this.camera.fov;

    if (this.transitionInterpolation === 'fpsSlerp') {
      // Position: linear lerp endpoint-to-endpoint (walk-up pose is
      // already collision-clamped).
      const position = _transitionScratchPos.lerpVectors(
        this.startPosition,
        this.endPosition,
        easedProgress,
      );
      if (
        this.transitionApplyHeightClamp
        && (this.cameraConfig.clampMinHeight !== -Infinity
          || this.cameraConfig.clampMaxHeight !== Infinity)
      ) {
        position.y = THREE.MathUtils.clamp(
          position.y,
          this.cameraConfig.clampMinHeight,
          this.cameraConfig.clampMaxHeight,
        );
      }
      this.camera.position.copy(position);

      // Orientation: slerp the precomputed start/end quaternions. Skips
      // `controls.lookAt` entirely — that would overwrite the slerped
      // quaternion with a `lookAt(lerpedTarget)` result, defeating the
      // purpose. After the transition completes the final-frame branch
      // syncs FirstPersonControls' internal `_lat/_lon` from the final
      // quaternion.
      this.camera.quaternion.slerpQuaternions(
        this.startQuaternion,
        this.endQuaternion,
        easedProgress,
      );

      if (fov !== this.camera.fov) {
        this.camera.fov = fov;
        this.camera.updateProjectionMatrix();
      }

      // Maintain `lastPose*` so `syncPoseFromCamera` post-transition
      // produces consistent state. FPS lastPoseTarget convention:
      // position + unit forward.
      this.lastPosePosition.copy(position);
      _transitionScratchForward.set(0, 0, -1).applyQuaternion(this.camera.quaternion);
      this.lastPoseTarget.copy(position).add(_transitionScratchForward);
      this.lastPoseFov = fov;
      this.hasPose = true;
    } else {
      const currentTarget = _transitionScratchTarget.lerpVectors(
        this.startTarget,
        this.endTarget,
        easedProgress,
      );

      let interpolatedPosition: THREE.Vector3;
      if (this.transitionInterpolation === 'orbitArc') {
        // Spherical lerp around the moving target. `azimuthDelta` is the
        // signed short-arc delta precomputed in `transitionToPose`.
        const radius = THREE.MathUtils.lerp(
          this.startSpherical.radius,
          this.endSpherical.radius,
          easedProgress,
        );
        const azimuth = this.startSpherical.azimuth + this.azimuthDelta * easedProgress;
        const polar = THREE.MathUtils.lerp(
          this.startSpherical.polar,
          this.endSpherical.polar,
          easedProgress,
        );
        interpolatedPosition = sphericalToCartesian(
          currentTarget,
          { radius, azimuth, polar },
          _transitionScratchPos,
        );
      } else {
        interpolatedPosition = _transitionScratchPos.lerpVectors(
          this.startPosition,
          this.endPosition,
          easedProgress,
        );
      }

      // Per-frame height clamp only matters for linear mode (orbit-arc has
      // the endpoint pre-clamped in `transitionToPose` and the arc stays in
      // the band by construction).
      const heightClampThisFrame =
        this.transitionInterpolation === 'orbitArc'
          ? false
          : this.transitionApplyHeightClamp;

      this.applyPose(
        { position: interpolatedPosition, target: currentTarget, fov },
        this.transitionApplyVerticalConstraint,
        heightClampThisFrame,
      );
    }

    // Note: we intentionally DO NOT re-enable `controls.enabled` mid-
    // transition. The render loop in `createScene` runs
    // `sightManager.update()` then `controls.update(delta)` on the same
    // frame; once `controls.enabled === true`, `controls.update()` calls
    // Three.js OrbitControls.update() (spherical roundtrip + damping +
    // built-in clamps) AND our `applyConstraints()` (collision / Y-clamp
    // / panBoundsXZ). With `isTransitioning` still true, the very next
    // frame `sightManager.update` overwrites `camera.position` again,
    // producing a visible "film-cut" jitter. The quadratic `ease-out`
    // combined with travel-scaled duration already keeps the perceptual
    // tail short enough to make an early release unnecessary.

    if (progress >= 1.0) {
      this.isTransitioning = false;
      this.controls.enabled = this.controlsWasEnabled;
      // FPS slerp wrote camera.quaternion directly, bypassing
      // FirstPersonControls. Sync its internal `_lat/_lon` from the final
      // quaternion so the very next `firstPerson.update()` doesn't snap
      // orientation back to the pre-transition values.
      if (this.transitionInterpolation === 'fpsSlerp') {
        this.controls.firstPerson.syncOrientationFromCamera();
      }
      // Drain any pending orbit rotational momentum (Three.js OrbitControls
      // sphericalDelta) that accumulated before the transition started but
      // wasn't applied because controls.enabled was false during the lerp.
      // Without this, the next orbit.update() would shift the camera away
      // from the freshly-applied endPose by the residual delta — visible as
      // a small (~5°) azimuth offset after pose transitions following an
      // auto-rotate / rotateLeft phase (e.g. preview tour → splat reveal).
      this.controls.freezeOrbitMomentum();
      const complete = this.onComplete;
      if (complete) {
        complete();
        this.onComplete = undefined;
      }
    }
  }

  private updateOrbitAnimation(currentTime: number): void {
    if (!this.orbitAnimationConfig) {
      return;
    }

    const {
      sightPosition,
      distance,
      height,
      azimuthMin,
      azimuthMax,
      polarMin,
      polarMax,
    } = this.orbitAnimationConfig;

    const azimuthMid = (azimuthMin + azimuthMax) * 0.5;
    const azimuthAmplitude = (azimuthMax - azimuthMin) * 0.5;
    const polarMid = (polarMin + polarMax) * 0.5;
    const polarAmplitude = (polarMax - polarMin) * 0.5;
    const angularSpeed = (Math.PI * 2) / this.orbitAnimationPeriod;
    const phase =
      (currentTime - this.orbitAnimationStartTime) * angularSpeed + this.orbitAnimationPhaseOffset;

    const azimuth = azimuthMid + Math.cos(phase) * azimuthAmplitude;
    const polar = polarMid + Math.sin(phase) * polarAmplitude;

    const x = distance * Math.sin(polar) * Math.cos(azimuth);
    const y = distance * Math.cos(polar) + height;
    const z = distance * Math.sin(polar) * Math.sin(azimuth);

    this.orbitAnimationPosition.set(
      sightPosition.x + x,
      sightPosition.y + y,
      sightPosition.z + z
    );
    let nextPosition = this.orbitAnimationPosition;
    if (this.orbitAnimationBlendStartTime < 0) {
      this.orbitAnimationBlendStartTime = currentTime;
    }
    const blendElapsed = currentTime - this.orbitAnimationBlendStartTime;
    if (blendElapsed < this.orbitAnimationBlendDuration) {
      const blendProgress = Math.min(
        blendElapsed / Math.max(this.orbitAnimationBlendDuration, 0.0001),
        1
      );
      const eased = easeInOutCubic(blendProgress);
      this.orbitAnimationBlendedPosition.lerpVectors(
        this.orbitAnimationBlendStartPosition,
        this.orbitAnimationPosition,
        eased
      );
      nextPosition = this.orbitAnimationBlendedPosition;
    }

    let nextTarget = this.orbitAnimationTarget;
    if (blendElapsed < this.orbitAnimationBlendDuration) {
      const blendProgress = Math.min(
        blendElapsed / Math.max(this.orbitAnimationBlendDuration, 0.0001),
        1
      );
      const eased = easeInOutCubic(blendProgress);
      this.orbitAnimationBlendedTarget.lerpVectors(
        this.orbitAnimationBlendStartTarget,
        this.orbitAnimationTarget,
        eased
      );
      nextTarget = this.orbitAnimationBlendedTarget;
    }

    this.applyPose({ position: nextPosition, target: nextTarget }, false);
  }

  private applyPose(pose: CameraPose, applyVerticalConstraint: boolean = true, applyHeightClamp: boolean = true): void {
    const constrainedPosition = pose.position.clone();
    if (
      applyHeightClamp &&
      (this.cameraConfig.clampMinHeight !== -Infinity ||
       this.cameraConfig.clampMaxHeight !== Infinity)
    ) {
      constrainedPosition.y = THREE.MathUtils.clamp(
        constrainedPosition.y,
        this.cameraConfig.clampMinHeight,
        this.cameraConfig.clampMaxHeight
      );
    }

    let constrainedTarget = pose.target.clone();
    if (applyVerticalConstraint && this.cameraConfig.constrainVertical) {
      constrainedTarget = this.constrainTargetVerticalAngle(
        constrainedPosition,
        constrainedTarget
      );
    }

    this.camera.position.copy(constrainedPosition);
    this.controls.lookAt(constrainedTarget);
    // In FPS, `setOrbitTargetOverride` would call `orbit.setTarget()` →
    // `OrbitControls.update()`, which silently applies min/maxPolarAngle clamps
    // even while orbit is disabled. For scenes with polar limits (e.g. Mina's
    // 70° min), this would yank the camera back up after every FPS pose change.
    // Orbit target is meaningless in FPS; skip the call.
    if (this.controls.viewMode === 'orbit') {
      this.controls.setOrbitTargetOverride(constrainedTarget);
    }

    if (pose.fov !== undefined && pose.fov !== this.camera.fov) {
      this.camera.fov = pose.fov;
      this.camera.updateProjectionMatrix();
    }

    this.lastPosePosition.copy(constrainedPosition);
    this.lastPoseTarget.copy(constrainedTarget);
    this.lastPoseFov = pose.fov ?? this.camera.fov;
    this.hasPose = true;
  }

  private applyCameraConstraints(): void {
    if (
      this.cameraConfig.clampMinHeight !== -Infinity ||
      this.cameraConfig.clampMaxHeight !== Infinity
    ) {
      this.camera.position.y = THREE.MathUtils.clamp(
        this.camera.position.y,
        this.cameraConfig.clampMinHeight,
        this.cameraConfig.clampMaxHeight
      );
    }

    if (this.cameraConfig.constrainVertical) {
      const direction = new THREE.Vector3(0, 0, -1).applyQuaternion(
        this.camera.quaternion
      );
      const currentTarget = this.camera.position.clone().add(direction);
      const constrainedTarget = this.constrainTargetVerticalAngle(
        this.camera.position,
        currentTarget
      );
      this.controls.lookAt(constrainedTarget);
    }
  }

  private constrainTargetVerticalAngle(
    cameraPosition: THREE.Vector3,
    target: THREE.Vector3
  ): THREE.Vector3 {
    const direction = new THREE.Vector3().subVectors(target, cameraPosition);
    const spherical = new THREE.Spherical();
    spherical.setFromVector3(direction);

    spherical.phi = THREE.MathUtils.clamp(
      spherical.phi,
      this.cameraConfig.verticalMin,
      this.cameraConfig.verticalMax
    );

    const constrainedDirection = new THREE.Vector3().setFromSpherical(spherical);
    return cameraPosition
      .clone()
      .add(constrainedDirection.normalize().multiplyScalar(direction.length()));
  }

  private findClosestOrbitPhase(config: OrbitAnimationConfig): number {
    const sampleCount = 48;
    const twoPi = Math.PI * 2;
    const azimuthMid = (config.azimuthMin + config.azimuthMax) * 0.5;
    const azimuthAmplitude = (config.azimuthMax - config.azimuthMin) * 0.5;
    const polarMid = (config.polarMin + config.polarMax) * 0.5;
    const polarAmplitude = (config.polarMax - config.polarMin) * 0.5;
    let bestPhase = 0;
    let bestDistanceSq = Infinity;

    for (let i = 0; i < sampleCount; i += 1) {
      const phase = (i / sampleCount) * twoPi;
      const azimuth = azimuthMid + Math.cos(phase) * azimuthAmplitude;
      const polar = polarMid + Math.sin(phase) * polarAmplitude;
      const x = config.distance * Math.sin(polar) * Math.cos(azimuth);
      const y = config.distance * Math.cos(polar) + config.height;
      const z = config.distance * Math.sin(polar) * Math.sin(azimuth);
      const distanceSq = this.camera.position.distanceToSquared(
        this.orbitAnimationPosition.set(
          config.sightPosition.x + x,
          config.sightPosition.y + y,
          config.sightPosition.z + z
        )
      );
      if (distanceSq < bestDistanceSq) {
        bestDistanceSq = distanceSq;
        bestPhase = phase;
      }
    }

    return bestPhase;
  }

  private updateOrbitAnimationBlendDuration(): void {
    if (!this.orbitAnimationConfig) {
      return;
    }
    const { sightPosition, distance, height, azimuthMin, azimuthMax, polarMin, polarMax } =
      this.orbitAnimationConfig;
    const azimuthMid = (azimuthMin + azimuthMax) * 0.5;
    const azimuthAmplitude = (azimuthMax - azimuthMin) * 0.5;
    const polarMid = (polarMin + polarMax) * 0.5;
    const polarAmplitude = (polarMax - polarMin) * 0.5;
    const azimuth = azimuthMid + Math.cos(this.orbitAnimationPhaseOffset) * azimuthAmplitude;
    const polar = polarMid + Math.sin(this.orbitAnimationPhaseOffset) * polarAmplitude;
    const x = distance * Math.sin(polar) * Math.cos(azimuth);
    const y = distance * Math.cos(polar) + height;
    const z = distance * Math.sin(polar) * Math.sin(azimuth);

    this.orbitAnimationPosition.set(
      sightPosition.x + x,
      sightPosition.y + y,
      sightPosition.z + z
    );

    const blendDistance = this.orbitAnimationPosition.distanceTo(this.camera.position);
    const angularSpeed = (Math.PI * 2) / this.orbitAnimationPeriod;
    const orbitSpeed = Math.max(Math.abs(angularSpeed * Math.max(distance, 0.001)), 0.001);
    this.orbitAnimationBlendDuration = blendDistance / orbitSpeed;
  }

  private applyEasing(t: number): number {
    switch (this.transitionEasing) {
      case 'ease-out':
        return easeOutCubic(t);
      case 'ease-in-out':
      default:
        return easeInOutCubic(t);
    }
  }
}
